import React, { useState, useEffect } from "react";
import { getNaray8WriterFollowing } from "../../../redux/naray8/naray8Action";
import { toast } from "react-toastify";
import defaultAvatar from "../../../assests/defaultImage.png";
import { useSocket } from "../../../hooks/useSocket";
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import { sendMessage } from '../../../redux/chat/chatReducer';

// Use correct production domain with www
const WEB_APP_URL = process.env.REACT_APP_WEB_APP_URL || 'https://www.blackdefynition.com';

export default function ShareUsersModal({ open, onClose, storyTitle = "", sharedText = "", contentType = "story", contentId = null, narrateId = null, onShareComplete }) {
  const { socket, isConnected } = useSocket();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [sharing, setSharing] = useState(false);
  const [shareStatus, setShareStatus] = useState({});

  useEffect(() => {
    if (open) {
      fetchFollowingUsers();
      setShareStatus({});
    } else {
      setSearchTerm("");
      setSelectedUsers(new Set());
      setFilteredUsers([]);
      setShareStatus({});
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchFollowingUsers = async () => {
    try {
      setLoading(true);
      let allUsers = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await getNaray8WriterFollowing({ page: currentPage, limit: 50 });
        const usersData = response.data || [];
        
        if (usersData.length > 0) {
          allUsers = [...allUsers, ...usersData];
          currentPage++;
          hasMore = usersData.length === 50;
        } else {
          hasMore = false;
        }
      }
      
      const uniqueUsers = Array.from(
        new Map(allUsers.map(user => [user.userId, user])).values()
      );
      
      setUsers(uniqueUsers);
      setFilteredUsers(uniqueUsers);
    } catch (error) {
      console.error("Error fetching following users:", error);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Create share message with correct URL based on content type
  const createShareMessage = () => {
    const profileId = contentId || userData?.userId;
    
    let displayName = storyTitle;
    if (!displayName || displayName === "null") {
      displayName = userData?.firstName && userData?.lastName 
        ? `${userData.firstName} ${userData.lastName}` 
        : "a user";
    }
    
    let shareUrl;
    
    // Build correct URL based on content type
    if (contentType === "chapter" && narrateId && contentId) {
      // For chapters: /naray8/b/{narrateId}/chapter/{chapterId}
      shareUrl = `${WEB_APP_URL}/naray8/b/${narrateId}/chapter/${contentId}`;
    } else if (contentType === "story" && contentId) {
      // For stories/books: /naray8/b/{narrateId}
      shareUrl = `${WEB_APP_URL}/naray8/b/${contentId}`;
    } else {
      // Default to author profile
      shareUrl = `${WEB_APP_URL}/naray8/writer/${profileId}`;
    }
    
    return `📚 Check out ${displayName} on Narray8!\n\n${shareUrl}`;
  };

  // Single user share with socket confirmation
  const handleSendToUser = async (userId, userFirstName, userLastName) => {
    if (!socket || !isConnected) {
      toast.error("Connection error. Please try again.");
      return;
    }

    const messageContent = createShareMessage();
    const messageId = `share_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    try {
      setShareStatus(prev => ({ ...prev, [userId]: { status: "sending", name: `${userFirstName} ${userLastName}` } }));
      
      const message = {
        messageId: messageId,
        content: messageContent,
        type: "text",
        senderId: userData?.userId,
        receiverId: userId,
        timestamp: new Date().toISOString(),
        localCreatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        chatType: "text",
        sent: true,
        delivered: false,
        read: false,
        metadata: {
          contentType: contentType,
          contentId: contentId || userData?.userId,
          narrateId: narrateId || null,
          title: storyTitle,
          sharedFrom: "naray8",
          isShared: true
        }
      };
      
      const roomId = `${userId}_${userData?.userId}`;
      
      const listData = {
        user: {
          userId: userId,
          firstName: userFirstName,
          lastName: userLastName,
          profilePhoto: []
        },
        room: roomId,
        lastMessage: {
          content: messageContent,
          createdAt: new Date().toISOString(),
          localCreatedAt: new Date().toISOString(),
          chatType: "text",
          messageId: messageId,
          senderId: userData?.userId,
          receiverId: userId
        },
        unreadCount: 1,
        archivedStatus: false
      };
      
      // Wait for socket confirmation before proceeding
      await new Promise((resolve, reject) => {
        socket.emit('chat message', { 
          senderId: userData?.userId, 
          receiverId: userId, 
          content: messageContent,
          reply: null
        }, (response) => {
          if (response && response.status === 'success') {
            resolve(response);
          } else {
            reject(new Error(response?.message || "Failed to send"));
          }
        });
        
        setTimeout(() => reject(new Error("Request timeout")), 10000);
      });
      
      dispatch(sendMessage({ roomId, message, listData }));
      
      setShareStatus(prev => ({ ...prev, [userId]: { status: "success", name: `${userFirstName} ${userLastName}` } }));
      toast.success(`Shared with ${userFirstName} ${userLastName}!`);
      
      if (selectedUsers.has(userId)) {
        const newSelected = new Set(selectedUsers);
        newSelected.delete(userId);
        setSelectedUsers(newSelected);
      }
      
      if (onShareComplete) {
        onShareComplete(userId, true);
      }
      
    } catch (error) {
      console.error("Share error:", error);
      setShareStatus(prev => ({ ...prev, [userId]: { status: "error", error: error.message, name: `${userFirstName} ${userLastName}` } }));
      toast.error(`Failed to share with ${userFirstName} ${userLastName}: ${error.message}`);
    }
  };

  // Bulk share with socket confirmation
  const handleShareAll = async () => {
    const targetUsers = selectedUsers.size > 0 
      ? filteredUsers.filter(u => selectedUsers.has(u.userId))
      : filteredUsers;
    
    if (targetUsers.length === 0) {
      toast.error("No users to share with");
      return;
    }
    
    if (!socket || !isConnected) {
      toast.error("Connection error. Please try again.");
      return;
    }
    
    const messageContent = createShareMessage();
    let successCount = 0;
    let failCount = 0;
    
    setSharing(true);
    
    for (const user of targetUsers) {
      const messageId = `share_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      
      try {
        setShareStatus(prev => ({ ...prev, [user.userId]: { status: "sending", name: `${user.firstName} ${user.lastName}` } }));
        
        const message = {
          messageId: messageId,
          content: messageContent,
          type: "text",
          senderId: userData?.userId,
          receiverId: user.userId,
          timestamp: new Date().toISOString(),
          localCreatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          chatType: "text",
          sent: true,
          delivered: false,
          read: false,
          metadata: {
            contentType: contentType,
            contentId: contentId || userData?.userId,
            narrateId: narrateId || null,
            title: storyTitle,
            sharedFrom: "naray8",
            isShared: true
          }
        };
        
        const roomId = `${user.userId}_${userData?.userId}`;
        
        const listData = {
          user: {
            userId: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            profilePhoto: user.profilePhoto || []
          },
          room: roomId,
          lastMessage: {
            content: messageContent,
            createdAt: new Date().toISOString(),
            localCreatedAt: new Date().toISOString(),
            chatType: "text",
            messageId: messageId,
            senderId: userData?.userId,
            receiverId: user.userId
          },
          unreadCount: 1,
          archivedStatus: false
        };
        
        await new Promise((resolve, reject) => {
          socket.emit('chat message', { 
            senderId: userData?.userId, 
            receiverId: user.userId, 
            content: messageContent,
            reply: null
          }, (response) => {
            if (response && response.status === 'success') {
              resolve(response);
            } else {
              reject(new Error(response?.message || "Failed to send"));
            }
          });
          
          setTimeout(() => reject(new Error("Request timeout")), 10000);
        });
        
        dispatch(sendMessage({ roomId, message, listData }));
        
        setShareStatus(prev => ({ ...prev, [user.userId]: { status: "success", name: `${user.firstName} ${user.lastName}` } }));
        successCount++;
        
      } catch (error) {
        console.error(`Failed to share with ${user.userId}:`, error);
        setShareStatus(prev => ({ ...prev, [user.userId]: { status: "error", error: error.message || "Failed", name: `${user.firstName} ${user.lastName}` } }));
        failCount++;
      }
    }
    
    setSharing(false);
    
    if (successCount > 0) {
      toast.success(`Shared with ${successCount} user${successCount !== 1 ? 's' : ''}!`);
    }
    if (failCount > 0) {
      toast.error(`Failed to share with ${failCount} user${failCount !== 1 ? 's' : ''}`);
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const selectAllUsers = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.userId)));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1f1f1f] w-11/12 max-w-md rounded-2xl p-6 border border-[#333] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white">Share</h2>
          <p className="text-gray-400 text-sm mt-1">Share with users you follow</p>
          {storyTitle && storyTitle !== "null" && (
            <div className="mt-2 p-2 bg-[#2a2a2a] rounded-lg">
              <p className="text-gray-300 text-xs line-clamp-2">
                <span className="text-[#BE9524]">Sharing:</span> {storyTitle}
              </p>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#2a2a2a] text-white pl-10 pr-4 py-2.5 rounded-lg border border-[#3a3a3a] focus:outline-none focus:border-[#BE9524] text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 px-1">
          <span className="text-gray-400 text-sm font-medium">{searchTerm ? "Search Results" : "Following"}</span>
          <div className="flex items-center gap-3">
            {filteredUsers.length > 0 && (
              <button onClick={selectAllUsers} className="text-xs text-[#BE9524] hover:text-[#a67c1a] transition-colors">
                {selectedUsers.size === filteredUsers.length ? "Deselect All" : "Select All"}
              </button>
            )}
            <span className="text-gray-500 text-xs">{filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"}</span>
          </div>
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto pr-1 mb-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BE9524] mx-auto mb-2"></div>
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {searchTerm ? "No users found" : "You're not following any users yet"}
            </div>
          ) : (
            filteredUsers.map((user) => {
              const status = shareStatus[user.userId];
              const isSelected = selectedUsers.has(user.userId);
              
              return (
                <div key={user.userId} className={`flex items-center justify-between bg-[#2a2a2a] hover:bg-[#333] px-4 py-3 rounded-lg transition-colors ${isSelected ? 'ring-1 ring-[#BE9524]' : ''} ${status?.status === 'success' ? 'ring-1 ring-green-500' : ''} ${status?.status === 'error' ? 'ring-1 ring-red-500' : ''}`}>
                  <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => toggleUserSelection(user.userId)}>
                    <img src={user.profilePhoto?.[0]?.photoPublicUrl || defaultAvatar} alt={user.firstName} className="w-8 h-8 rounded-full object-cover" onError={(e) => { e.target.src = defaultAvatar; }} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{user.firstName} {user.lastName}</span>
                        {status?.status === 'sending' && <span className="text-yellow-500 text-xs animate-pulse">sending...</span>}
                        {status?.status === 'success' && <span className="text-green-500 text-xs">✓ sent</span>}
                        {status?.status === 'error' && <span className="text-red-500 text-xs">failed</span>}
                      </div>
                      {user.UserName && <p className="text-gray-400 text-xs">@{user.UserName}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleUserSelection(user.userId)} disabled={status?.status === 'sending' || status?.status === 'success'} className="w-4 h-4 rounded border-gray-500 bg-[#2a2a2a] text-[#BE9524] focus:ring-[#BE9524] disabled:opacity-50" />
                    <button onClick={() => handleSendToUser(user.userId, user.firstName, user.lastName)} disabled={sharing || status?.status === 'sending' || status?.status === 'success'} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${status?.status === 'success' ? 'bg-green-600 text-white cursor-default' : 'bg-[#BE9524] hover:bg-[#a67c1a] text-white'}`}>
                      {status?.status === 'sending' ? 'Sending...' : status?.status === 'success' ? 'Sent' : 'Send'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {selectedUsers.size > 0 && (
          <div className="mb-3 px-1">
            <span className="text-[#BE9524] text-sm">{selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected</span>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 bg-[#2a2a2a] hover:bg-[#333] text-white rounded-lg font-medium transition-colors">Cancel</button>
          <button onClick={handleShareAll} disabled={filteredUsers.length === 0 || sharing || loading} className="flex-1 py-3 bg-[#BE9524] hover:bg-[#a67c1a] text-white rounded-lg font-medium transition-all disabled:opacity-50">
            {sharing ? "Sharing..." : `Share All (${selectedUsers.size > 0 ? selectedUsers.size : filteredUsers.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}